import type { Settings, StreamHeader } from './reliableStream'
import { type OutgoingFrame, StreamEnd } from './streamEnd'

export type { OutgoingFrame } from './streamEnd'

/**
 * A terminal session on the reliable transport (docs/GatewayReliableTransport.md):
 * the browser is the stream's end, so keystrokes and output arrive once and in
 * order across the gateway's stream rolls, where the old path could lose or
 * swap them.
 *
 * Keystrokes the stream's bound can't take yet wait in order rather than
 * being dropped; [`StreamEnd`] holds them, with the stream and its timer.
 */

/** What the gateway sends on a terminal session's stream, as the backend relays it. */
export type TerminalFrameMessage =
  | { kind: 'data'; data: string }
  | { kind: 'event'; eventType: number; message: string }

export interface TerminalStreamOptions {
  sessionId: string
  /** Put a frame on the websocket. */
  send: (frame: OutgoingFrame) => void
  /** The device's output, as base64. */
  onData: (data: string) => void
  /** The session ended on the gateway, with its reason. */
  onEvent: (eventType: number, message: string) => void
  /** The stream gave up or the gateway restarted: the session is gone. */
  onLost: () => void
  now?: () => number
  settings?: Settings
}

const costOf = (m: string | TerminalFrameMessage) =>
  typeof m === 'string' ? m.length : m.kind === 'data' ? m.data.length : m.message.length

export class TerminalStream {
  private readonly end: StreamEnd<TerminalFrameMessage>

  constructor(opts: TerminalStreamOptions) {
    this.end = new StreamEnd<TerminalFrameMessage>({
      sessionId: opts.sessionId,
      send: opts.send,
      onMessage: (m) => {
        if (m.kind === 'data') opts.onData(m.data)
        else opts.onEvent(m.eventType, m.message)
      },
      onLost: opts.onLost,
      costOf,
      now: opts.now,
      settings: opts.settings,
    })
  }

  /** Keystrokes, as base64. */
  input(data: string): void {
    this.end.input(data)
  }

  /** A frame the backend relayed from the gateway. */
  receive(header: StreamHeader, message: TerminalFrameMessage | null | undefined): void {
    this.end.receive(header, message)
  }

  dispose(): void {
    this.end.dispose()
  }
}
